import { Injectable } from '@angular/core';
import { Observable, Subject, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { StatopDal } from '../dal/statop.dal';
import { activityData, PilotageCachedPayload } from '../interfaces/statop.interfaces';
import { HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class StatopService {
  private readonly triggerGetAllIndicatorsSource = new Subject<void>();

  private readonly cache = new Map<string, PilotageCachedPayload>();

  public triggerGetAllIndicators$ = this.triggerGetAllIndicatorsSource.asObservable();

  constructor(private readonly statopDal: StatopDal) {}

  triggerGetAllIndicators(): void {
    this.triggerGetAllIndicatorsSource.next();
  }

  get(key: string): PilotageCachedPayload | null {
    return this.cache.get(key) ?? null;
  }

  set(payload: PilotageCachedPayload): void {
    this.cache.set(payload.key, payload);
  }

  clear(key: string): void {
    this.cache.delete(key);
  }

  clearAll(): void {
    this.cache.clear();
  }

  //////////////////
  // General      //
  //////////////////
  // First Row

  getOverallStats(
    fromDate: string,
    toDate: string,
    lastPreviousDates?: boolean,
    specialties?: string[],
    rooms?: string[],
    hospitalId?: string,
    deleteCache?: boolean,
    getCachedStatsOnly?: boolean
  ): Observable<any> {
    return this.statopDal.getOverallStats(
      fromDate,
      toDate,
      lastPreviousDates,
      specialties,
      rooms,
      hospitalId,
      deleteCache,
      getCachedStatsOnly
    );
  }

  getStatsPerRoom(
    fromDate: string,
    toDate: string,
    roomId: string,
    specialties?: string[],
    lastPreviousDates?: boolean,
    deleteCache?: boolean
  ): Observable<any> {
    return this.statopDal.getStatsPerRoom(fromDate, toDate, roomId, specialties, lastPreviousDates, deleteCache);
  }

  getStatsPerSpecialty(
    fromDate: string,
    toDate: string,
    specialtyId: string,
    isGeneralSurgeon: string,
    rooms?: string[],
    lastPreviousDates?: boolean,
    emergencies?: string,
    hospitalId?: string,
    deleteCache?: boolean
  ): Observable<any> {
    return this.statopDal.getStatsPerSpecialty(
      fromDate,
      toDate,
      specialtyId,
      isGeneralSurgeon,
      rooms,
      lastPreviousDates,
      emergencies,
      hospitalId,
      deleteCache
    );
  }

  getStatsPerSurgeon(
    fromDate: string,
    toDate: string,
    surgeonId: string,
    roomId?: string,
    lastPreviousDates?: boolean,
    hospitalVacationsStartTime?: string,
    hospitalVacationsEndTime?: string,
    deleteCache?: boolean
  ): Observable<any> {
    return this.statopDal.getStatsPerSurgeon(
      fromDate,
      toDate,
      surgeonId,
      roomId,
      lastPreviousDates,
      hospitalVacationsStartTime,
      hospitalVacationsEndTime,
      deleteCache
    );
  }

  getOverallStatsBySurgeon(
    fromDate: string,
    toDate: string,
    hospitalId: string,
    lastPreviousDates?: boolean,
    deleteCache?: boolean,
    getCachedStatsOnly?: boolean
  ): Observable<any> {
    return this.statopDal.getOverallStatsBySurgeon(
      fromDate,
      toDate,
      hospitalId,
      lastPreviousDates,
      deleteCache,
      getCachedStatsOnly
    );
  }

  getOverallStatsGroupedByType(
    fromDate: string,
    toDate: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string,
    hospitalId: string,
    currentView: string,
    createTask: boolean,
    taskName: string,
    data: any,
    periodicity?: string,
    isComparisonEnabled?: boolean
  ): Observable<HttpResponse<Blob>> {
    return this.statopDal.getOverallStatsGroupedByType(
      fromDate,
      toDate,
      hospitalVacationsStartTime,
      hospitalVacationsEndTime,
      hospitalId,
      currentView,
      createTask,
      taskName,
      data,
      periodicity,
      isComparisonEnabled
    );
  }

  getActivityData(
    fromDate: string,
    toDate: string,
    profilesType: string | undefined,
    hospitalId: string,
    surgeonId: string | undefined,
    roomId: string | undefined,
    specialtyId: string | undefined,
    limit: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string,
    delayTime: number = 0
  ): Observable<activityData> {
    return timer(delayTime).pipe(
      switchMap(() =>
        this.statopDal.getActivityData(
          fromDate,
          toDate,
          profilesType,
          hospitalId,
          surgeonId,
          roomId,
          specialtyId,
          limit,
          hospitalVacationsStartTime,
          hospitalVacationsEndTime
        )
      )
    );
  }

  getRoomAnalysisData(
    fromDate: string,
    toDate: string,
    roomId: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string
  ): Observable<any> {
    return this.statopDal.getRoomAnalysisData(
      fromDate,
      toDate,
      roomId,
      hospitalVacationsStartTime,
      hospitalVacationsEndTime
    );
  }

  getSpecialtyAnalysisData(
    fromDate: string,
    toDate: string,
    specialtyId: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string,
    isGeneralSurgeon?: boolean
  ): Observable<any> {
    return this.statopDal.getSpecialtyAnalysisData(
      fromDate,
      toDate,
      specialtyId,
      hospitalVacationsStartTime,
      hospitalVacationsEndTime,
      isGeneralSurgeon
    );
  }

  getStatOpInterventionsTotal(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getStatOpInterventionsTotal(startDate, endDate);
  }

  getStatOpInterventionsProgress(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getStatOpInterventionsProgress(startDate, endDate);
  }

  getStatOpInterventionsPerDay(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getStatOpInterventionsPerDay(startDate, endDate);
  }

  // Second Row

  getStatOpInterventionsPerSpecialty(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getStatOpInterventionsPerSpecialty(startDate, endDate);
  }

  getStatOpInterventionsTopSpecialty(startDate: string, endDate: string, limit: string): Observable<any> {
    return this.statopDal.getStatOpInterventionsTopSpecialty(startDate, endDate, limit);
  }

  // Third Row

  getRoomData(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.statopDal.getRoomData(startDate, endDate, roomId);
  }

  getRoomTop(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.statopDal.getRoomTop(startDate, endDate, roomId);
  }

  getSpecialtyTop(startDate: string, endDate: string, specialtyId: string): Observable<any> {
    return this.statopDal.getSpecialtyTop(startDate, endDate, specialtyId);
  }

  getRatesHeader(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.statopDal.getRatesHeader(startDate, endDate, roomId);
  }

  getRatesHeaderSpecialty(startDate: string, endDate: string, specialtyId: string): Observable<any> {
    return this.statopDal.getRatesHeaderSpecialty(startDate, endDate, specialtyId);
  }

  // getRoomEffectivenessRatePerDay(startDate: string, endDate: string, roomId: string): Observable<any> {
  //   return this.statopDal.getRoomEffectivenessRatePerDay(startDate, endDate, roomId);
  // }

  // getRoomEffectivenessRate(startDate: string, endDate: string, roomId: string): Observable<any> {
  //   return this.statopDal.getRoomEffectivenessRate(startDate, endDate, roomId);
  // }

  getRoomsBubblesData(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getRoomsBubblesData(startDate, endDate);
  }

  getStatOpBoxPlot(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getStatOpBoxPlot(startDate, endDate);
  }

  //////////////////
  // One Surgeon  //
  //////////////////

  getOneSurgeonFirstGeneralData(
    startDate: string,
    endDate: string,
    surgeonId: string,
    hospital?: string
  ): Observable<any> {
    return this.statopDal.getOneSurgeonFirstGeneralData(startDate, endDate, surgeonId, hospital);
  }

  // Fifth Row Row

  getOneSurgeonStatOpInterventionsTotal(
    startDate: string,
    endDate: string,
    surgeonId: string,
    roomId?: string
  ): Observable<any> {
    return this.statopDal.getOneSurgeonStatOpInterventionsTotal(startDate, endDate, surgeonId, roomId);
  }

  getOneSurgeonStatOpInterventionsProgress(startDate: string, endDate: string, surgeonId: string): Observable<any> {
    return this.statopDal.getOneSurgeonStatOpInterventionsProgress(startDate, endDate, surgeonId);
  }

  getOneSurgeonStatOpInterventionsPerDay(startDate: string, endDate: string, surgeonId: string): Observable<any> {
    return this.statopDal.getOneSurgeonStatOpInterventionsPerDay(startDate, endDate, surgeonId);
  }

  getOneSurgeonStatOpInterventionsTotalPerRoom(
    startDate: string,
    endDate: string,
    surgeonId: string,
    roomId: string
  ): Observable<any> {
    return this.statopDal.getOneSurgeonStatOpInterventionsTotalPerRoom(startDate, endDate, surgeonId, roomId);
  }
  // Sixth Row

  // getOneSurgeonEffectivenessRate(startDate: string, endDate: string, surgeonId: string): Observable<any> {
  //   return this.statopDal.getOneSurgeonEffectivenessRate(startDate, endDate, surgeonId);
  // }

  // getOneSurgeonEffectivenessRatePerDay(startDate: string, endDate: string, surgeonId: string): Observable<any> {
  //   return this.statopDal.getOneSurgeonEffectivenessRatePerDay(startDate, endDate, surgeonId);
  // }

  getAdditionRate(fromDate: string, toDate: string, surgeonId: string): Observable<any> {
    return this.statopDal.getAdditionRate(fromDate, toDate, surgeonId);
  }

  getOvertakingRate(fromDate: string, toDate: string, surgeonId: string): Observable<any> {
    return this.statopDal.getOvertakingRate(fromDate, toDate, surgeonId);
  }

  getCountsPerReasons(
    hospitalId: any,
    startDate: string,
    endDate: string,
    profilesType: string,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getCountsPerReasons(hospitalId, startDate, endDate, profilesType))
    );
  }

  getProfilesByReasonAndDate(
    hospitalId: string,
    date: string,
    reasonId: string,
    profilesType: string
  ): Observable<any> {
    return this.statopDal.getProfilesByReasonAndDate(hospitalId, date, reasonId, profilesType);
  }

  getStatOpEffectifPerSeniority(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getStatOpEffectifPerSeniority(startDate, endDate)));
  }

  getStatOpEffectifTotal(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getStatOpEffectifTotal(startDate, endDate)));
  }

  getStatOpResidencySeniorityDistribution(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getStatOpResidencySeniorityDistribution(startDate, endDate))
    );
  }

  getStatOpNeedsPerSpecialty(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getStatOpNeedsPerSpecialty(startDate, endDate)));
  }

  getStatOpAvailability(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getStatOpAvailability(startDate, endDate)));
  }

  getTopThreeNumberOfGuards(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getTopThreeNumberOfGuards(startDate, endDate)));
  }

  getStatOpAbsencePerReason(
    startDate: string,
    endDate: string,
    profile?: string,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getStatOpAbsencePerReason(startDate, endDate, profile))
    );
  }

  getStatOpExtracliniqueProfiles(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getStatOpExtracliniqueProfiles(startDate, endDate)));
  }

  getStatOpGeneral(
    startDate: string,
    endDate: string,
    type: string,
    specialty: string | undefined,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getStatOpGeneral(startDate, endDate, type, specialty)));
  }

  getStatOpDistributionPerType(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.statopDal.getStatOpDistributionPerType(startDate, endDate, profileId);
  }

  getStatOpNumberOfGuards(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.statopDal.getStatOpNumberOfGuards(startDate, endDate, profileId);
  }

  getStatOpGuardDsitribution(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.statopDal.getStatOpGuardDsitribution(startDate, endDate, profileId);
  }

  getStatOpWeekendGuardDsitribution(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.statopDal.getStatOpWeekendGuardDsitribution(startDate, endDate, profileId);
  }

  getSurgeonsOverflowRate(startDate: string, endDate: string, hasToValidate?: boolean): Observable<any> {
    return this.statopDal.getSurgeonsOverflowRate(startDate, endDate, hasToValidate);
  }

  getRatesPerDateAndRoom(startDate: string, endDate: string, hospitalId: string): Observable<any> {
    return this.statopDal.getRatesPerDateAndRoom(startDate, endDate, hospitalId);
  }

  /* ************************ */

  // TVO
  getTVO(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getTVO(startDate, endDate);
  }

  //TROS + TROV
  getTROS(startDate: string, endDate: string, withoutUrgences?: boolean): Observable<any> {
    return this.statopDal.getTROS(startDate, endDate, undefined, undefined, undefined, withoutUrgences);
  }

  getTROSAllRoomsBySurgeon(
    startDate: string,
    endDate: string,
    surgeonId: string,
    withoutUrgences?: boolean
  ): Observable<any> {
    return this.statopDal.getTROSAllRoomsBySurgeon(startDate, endDate, surgeonId, withoutUrgences);
  }

  // late Start
  getAverageLateStartRate(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getAverageLateStartRate(startDate, endDate);
  }

  // earlyEnd
  getAverageEarlyEndRate(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getAverageEarlyEndRate(startDate, endDate);
  }

  // isAmbu
  getIsAmbuRate(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getIsAmbuRate(startDate, endDate);
  }

  // isUrgences
  getIsUrgencesRate(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getIsUrgencesRate(startDate, endDate);
  }

  // interInterventions
  getTimeBetweenInterventions(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getTimeBetweenInterventions(startDate, endDate);
  }

  // Cleaning time
  getCleaningTime(startDate: string, endDate: string): Observable<any> {
    return this.statopDal.getCleaningTime(startDate, endDate);
  }

  getRoomHours(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.statopDal.getRoomHours(startDate, endDate, roomId);
  }

  getEquityPerDate(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getEquityPerDate(startDate, endDate)));
  }

  getGuardEquityPerDate(
    startDate: string,
    endDate: string,
    position: string,
    guardId: string,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getGuardEquityPerDate(startDate, endDate, position, guardId))
    );
  }

  getCodesCountData(
    startDate: string,
    endDate: string,
    position: string,
    guardId: string,
    period: string,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getCodesCountData(startDate, endDate, position, guardId, period))
    );
  }

  getInterventions(
    fromDate: string,
    toDate: string,
    hospitalId: any,
    delayTime: number = 0
  ): Observable<any[] | undefined> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getInterventions(fromDate, toDate, hospitalId)));
  }

  getActDetails(
    act: string,
    fromDate: string,
    toDate: string,
    hospitalId: any,
    delayTime: number = 0
  ): Observable<any[] | undefined> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getActDetails(act, fromDate, toDate, hospitalId)));
  }

  getMatchingProgrammedActs(
    act: string,
    fromDate: string,
    toDate: string,
    hospitalId: any,
    delayTime: number = 0
  ): Observable<any[] | undefined> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getMatchingProgrammedActs(act, fromDate, toDate, hospitalId))
    );
  }

  getTopInterventionsByCA(
    surgeonId: string,
    specialtyId: string,
    startDate: string,
    endDate: string,
    isAnesth: boolean,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getTopInterventionsByCA(surgeonId, specialtyId, startDate, endDate, isAnesth))
    );
  }

  getTotalCACosts(startDate: string, endDate: string, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getTotalCACosts(startDate, endDate)));
  }

  getVacationsCACosts(startDate: string, endDate: string, isAnesth: boolean, delayTime: number = 0): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getVacationsCACosts(startDate, endDate, isAnesth)));
  }

  getCA(
    surgeonId: string,
    specialtyId: string,
    startDate: string,
    endDate: string,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(switchMap(() => this.statopDal.getCA(surgeonId, specialtyId, startDate, endDate)));
  }

  getCACumul(
    surgeonId: string,
    specialtyId: string,
    startDate: string,
    endDate: string,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getCACumul(surgeonId, specialtyId, startDate, endDate))
    );
  }

  getTotalCACostsBySpe(
    surgeonId: string,
    specialtyId: string,
    startDate: string,
    endDate: string,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() => this.statopDal.getTotalCACostsBySpe(surgeonId, specialtyId, startDate, endDate))
    );
  }

  getTotalCostsByFormation(
    surgeonId: string,
    specialtyId: string,
    startDate: string,
    endDate: string,
    residency: string,
    isAnesth: boolean,
    delayTime: number = 0
  ): Observable<any> {
    return timer(delayTime).pipe(
      switchMap(() =>
        this.statopDal.getTotalCostsByFormation(surgeonId, specialtyId, startDate, endDate, residency, isAnesth)
      )
    );
  }

  getSurgeonOpeningsStats(
    fromDate: string,
    toDate: string,
    deleteCache: boolean,
    period: string,
    previousDates: boolean,
    surgeon?: string,
    rooms?: string[],
    specialties?: string[],
    day?: string,
    week?: string
  ): Observable<any> {
    return this.statopDal.getSurgeonOpeningsStats(
      fromDate,
      toDate,
      deleteCache,
      period,
      previousDates,
      surgeon,
      rooms,
      specialties,
      day,
      week
    );
  }

  downloadExport(taskId: string): Observable<HttpResponse<Blob>> {
    return this.statopDal.downloadExport(taskId);
  }
}
